// widget-installation-actions — imperative inline status-override UX for the
// Widget Studio installations surface.
//
// CHG-2463 / ITER-905205 — Widget Studio S4 (installations CRUD).
//
// Surface:
//   - `openInstallationStatusOverride(record)` — imperative call from the
//     generated widget-installation-detail.ts `actions` override-block onClick.
//     Opens a FormDialog to set the observational status (the only admin write:
//     PATCH /admin/widgets/installations/{id}; only status + metadata_json are
//     writable, an invalid status is a 400).
//   - `<WidgetInstallationActionsHost />` — mount once at the App root. Owns the
//     mutation + react-query invalidation and renders the dialog.
//
// Same single-host + module-emitter pattern as widget-instance-actions /
// bulk-tag-dialog, so the hookless generated config wires onClick without
// breaking rules-of-hooks. RBAC stays in the config (permissionGate); the BE
// re-enforces widget-platform.configure.

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { widgetsAdminApi } from "../api/widgets-admin";
import type { WidgetInstallationStatus } from "../api/widgets-admin";
import type { WidgetInstallation } from "../api/types";
import { FormDialog, type FormFieldDef } from "../components/composition/FormDialog";

// The observational-state value space (widget-installation-status.value-space).
const STATUS_OPTIONS: Array<{ value: WidgetInstallationStatus; label: string }> = [
  { value: "active", label: "Active" },
  { value: "observed", label: "Observed" },
  { value: "stale", label: "Stale" },
  { value: "removed", label: "Removed" },
  { value: "blocked", label: "Blocked" },
  { value: "error", label: "Error" },
];

const OVERRIDE_FIELDS: FormFieldDef[] = [
  {
    name: "status",
    type: "select",
    label: "Status",
    props: { options: STATUS_OPTIONS },
  },
  {
    name: "note",
    type: "textarea",
    label: "Note (optional)",
    props: { placeholder: "Why is the status being overridden? (stored in metadata)" },
  },
];

// ─────────────────────────────────────────────────────────────────
// Emitter: a singleton the host subscribes to. Mount exactly one host.
// ─────────────────────────────────────────────────────────────────

type Listener = (record: WidgetInstallation) => void;
let activeListener: Listener | null = null;

export function openInstallationStatusOverride(record: WidgetInstallation): void {
  if (!activeListener) return; // no-op if no host mounted
  activeListener(record);
}

export function WidgetInstallationActionsHost() {
  const [record, setRecord] = useState<WidgetInstallation | null>(null);

  useEffect(() => {
    activeListener = setRecord;
    return () => {
      activeListener = null;
    };
  }, []);

  if (!record) return null;
  return <StatusOverrideDialog record={record} onClose={() => setRecord(null)} />;
}

function StatusOverrideDialog({
  record,
  onClose,
}: {
  record: WidgetInstallation;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>({
    status: record.status,
    note: "",
  });
  const mutation = useMutation({
    mutationFn: (v: Record<string, unknown>) => {
      const note = String(v.note ?? "").trim();
      return widgetsAdminApi.updateInstallation(record.id, {
        status: v.status as WidgetInstallationStatus,
        metadata_json: note.length ? { note } : null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["widget-installations", "detail", record.id],
      });
      void queryClient.invalidateQueries({ queryKey: ["widget-installations", "list"] });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`Override status — ${record.domain}`}
      description="Set the observational status for this installation. Changes are admin overrides on top of the auto-observed state."
      fields={OVERRIDE_FIELDS}
      values={values}
      onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
      onSubmit={async (v) => {
        await mutation.mutateAsync(v);
      }}
      submitLabel="Save status"
      busy={mutation.isPending}
      error={mutation.isError ? "Could not update the status. Please try again." : undefined}
    />
  );
}
